'use client';

import {
  requestLocationMeetingPointTypeDescription,
  requestLocationMeetingPointTypeTitle,
  requestLocationMeetingPointTypeTypesHome,
  requestLocationMeetingPointTypeTypesHomeDescription,
  requestLocationMeetingPointTypeTypesHotel,
  requestLocationMeetingPointTypeTypesHotelDescription,
  requestLocationMeetingPointTypeTypesOther,
  requestLocationMeetingPointTypeTypesOtherDescription,
  requestLocationMeetingPointTypeTypesTravel,
  requestLocationMeetingPointTypeTypesTravelDescription,
  wizardNext,
} from '@bells/i18n';
import {
  AttendantSearchSingleResponse,
  VisitStatus,
} from '@bells/schema/models/attendant';
import { RequestLocationType } from '@bells/schema/models/mapbox';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Hotel, House, MapPinX, Plane } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';

import { useRequest } from '@/components/girl/request-context';
import {
  RequestFormSchema,
  type RequestForm,
} from '@bells/schema/models/request';

import { RequestSelectionBox } from '@bells/ui/components/selection-box';
import { useMemo } from 'react';

export const StepChooseLocationType = ({
  data,
  resolve,
}: {
  data: AttendantSearchSingleResponse;
  resolve?: (val: Pick<RequestForm, 'location'>) => void;
}) => {
  const request = useRequest();
  const form = useForm<Pick<RequestForm, 'location'>>({
    resolver: zodResolver(RequestFormSchema.pick({ location: true })),
    defaultValues: {
      location: request.values.location,
    },
    mode: 'all',
  });

  const handleSubmit = form.handleSubmit((val) => {
    request.setValues(val);

    if (resolve) {
      resolve(val);
    }
  });

  const readyToTravel = useMemo(() => {
    const countries = data.travelReadyCountries ?? [];

    return countries.length > 0;
  }, [data.travelReadyCountries]);

  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{requestLocationMeetingPointTypeTitle()}</DialogTitle>
        <DialogDescription>
          {requestLocationMeetingPointTypeDescription()}
        </DialogDescription>
      </DialogHeader>
      <div className='flex-1 p-6'>
        <FormProvider {...form}>
          <form className='w-full' onSubmit={handleSubmit}>
            <FormField
              control={form.control}
              name='location'
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormControl>
                    <div className='grid grid-cols-1 gap-4 pt-4 lg:grid-cols-2'>
                      <RequestSelectionBox
                        title={requestLocationMeetingPointTypeTypesHotel()}
                        icon={Hotel}
                        disabled={
                          !data.visits?.includes(VisitStatus.HOTEL_VISITS)
                        }
                        selected={field.value == RequestLocationType.HOTEL}
                        onSelect={() =>
                          field.onChange(RequestLocationType.HOTEL)
                        }
                        description={requestLocationMeetingPointTypeTypesHotelDescription()}
                      ></RequestSelectionBox>
                      <RequestSelectionBox
                        title={requestLocationMeetingPointTypeTypesHome()}
                        icon={House}
                        disabled={
                          !data.visits?.includes(VisitStatus.HOUSE_VISITS)
                        }
                        selected={
                          field.value == RequestLocationType.PRIVATE_ADDRESS
                        }
                        onSelect={() =>
                          field.onChange(RequestLocationType.PRIVATE_ADDRESS)
                        }
                        description={requestLocationMeetingPointTypeTypesHomeDescription()}
                      ></RequestSelectionBox>

                      <RequestSelectionBox
                        title={requestLocationMeetingPointTypeTypesTravel()}
                        icon={Plane}
                        disabled={!readyToTravel}
                        selected={field.value == RequestLocationType.TRAVEL}
                        onSelect={() =>
                          field.onChange(RequestLocationType.TRAVEL)
                        }
                        description={requestLocationMeetingPointTypeTypesTravelDescription()}
                      ></RequestSelectionBox>

                      <RequestSelectionBox
                        title={requestLocationMeetingPointTypeTypesOther()}
                        icon={MapPinX}
                        selected={field.value == RequestLocationType.UNKNOWN}
                        onSelect={() =>
                          field.onChange(RequestLocationType.UNKNOWN)
                        }
                        description={requestLocationMeetingPointTypeTypesOtherDescription()}
                      ></RequestSelectionBox>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>
      <DialogFooter className='gap-2 p-6 pt-0'>
        <Button
          disabled={!form.formState.isValid}
          onClick={handleSubmit}
          type='submit'
        >
          {wizardNext()}
        </Button>
      </DialogFooter>
    </>
  );
};
